import type {
  LockerverseCatalog,
  LockerverseCheckoutClient,
} from "@lockerverse/sdk/checkout";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { getAccentContrast, normalizeAccentColor } from "./accent-contrast.ts";
import { formatMoney } from "./format-money.ts";
import {
  defaultLockerverseStripeLoader,
  PaymentSkeleton,
  ReadyPayment,
} from "./payment-form.tsx";
import type { PaymentUiState } from "./payment-form-state.ts";
import type {
  LockerversePaymentProps,
  LockerversePaymentRecovery,
} from "./types.ts";
import { usePaymentCheckout } from "./use-payment-checkout.ts";

type PaymentViewProps = Omit<LockerversePaymentProps, "widget">;
const paymentCadence = { annually: "/year", monthly: "/month", "one-time": "" };

function PaymentCatalogHeader({
  branding,
  catalog,
}: {
  branding: LockerversePaymentProps["branding"] | null;
  catalog: LockerverseCatalog;
}) {
  return (
    <header>
      {catalog.widget.showCommunityBranding && branding ? (
        <div className="lockerverse-payment__branding">
          {branding.imageUrl ? (
            <img
              alt={branding.name}
              height="48"
              src={branding.imageUrl}
              width="48"
            />
          ) : null}
          <strong>{branding.name}</strong>
        </div>
      ) : null}
      <h1>{catalog.widget.title ?? catalog.widget.name}</h1>
      {catalog.widget.description ? <p>{catalog.widget.description}</p> : null}
    </header>
  );
}

function PaymentCatalogSummary({
  catalog,
  selection,
}: {
  catalog: LockerverseCatalog;
  selection: LockerversePaymentProps["selection"];
}) {
  const products = selection?.length
    ? catalog.products.filter((product) =>
        selection.some((item) => item.productSlug === product.slug)
      )
    : catalog.products;
  if (products.length === 0) {
    return null;
  }
  return (
    <div className="lockerverse-payment__payment-summary">
      <h2>Products</h2>
      <div className="lockerverse-payment__payment-summary-items">
        {products.map((product) => {
          const amount =
            product.pricingMode === "fixed"
              ? product.amountCents
              : product.minimumAmountCents;
          const cadence = paymentCadence[product.paymentOption];
          return (
            <div
              className="lockerverse-payment__payment-summary-row"
              key={product.id}
            >
              <span>{product.name}</span>
              <strong>
                {amount === null
                  ? "Choose amount"
                  : `${product.pricingMode === "custom" ? "From " : ""}${formatMoney(amount)}${cadence}`}
              </strong>
            </div>
          );
        })}
      </div>
      <p>Base prices. Final total is checked at checkout.</p>
    </div>
  );
}

function getPaymentAppearance(
  style: LockerversePaymentProps["style"],
  branding: LockerversePaymentProps["branding"] | null
) {
  const accent = normalizeAccentColor(
    style?.["--lockerverse-accent"] ?? branding?.accentColor ?? "#38d77a"
  );
  const accentContrast = getAccentContrast(accent);
  const rootStyle: LockerversePaymentProps["style"] = { ...style };
  if (style?.["--lockerverse-accent"] !== undefined || branding?.accentColor) {
    rootStyle["--lockerverse-accent"] = accent;
  }
  if (
    accentContrast &&
    style?.["--lockerverse-accent-contrast"] === undefined
  ) {
    rootStyle["--lockerverse-accent-contrast"] = accentContrast;
  }
  return { accent, rootStyle };
}

function LoadedPayment({
  catalog,
  client,
  loadError,
  onRetry,
  ...viewProps
}: PaymentViewProps & {
  catalog: LockerverseCatalog;
  client: LockerverseCheckoutClient;
  loadError?: Error | null;
  onRetry?: () => void;
}) {
  const {
    branding,
    className,
    initialCustomFieldValues,
    metadata,
    onPaymentComplete,
    onPaymentRecoveryChange,
    onPaymentUncertain,
    resumePayment,
    selection,
    style,
    stripeLoader = defaultLockerverseStripeLoader,
    theme = "dark",
    ...sectionProps
  } = viewProps;
  const [activeResumePayment, setActiveResumePayment] = useState(resumePayment);
  const appearanceRootRef = useRef<HTMLElement | null>(null);
  const [notice, setNotice] = useState<string>();
  const [paymentState, setPaymentState] = useState<PaymentUiState>("idle");
  const paymentStateRevisionRef = useRef<number | undefined>(undefined);
  const { selectionError, session, updateCheckout } = usePaymentCheckout({
    catalog,
    client,
    paymentState,
    selection,
  });
  const readySessionRevision =
    session.phase === "ready" ? session.revision : undefined;
  const handlePaymentStateChange = useCallback(
    (state: PaymentUiState) => {
      if (readySessionRevision !== undefined) {
        paymentStateRevisionRef.current = readySessionRevision;
      }
      setPaymentState(state);
    },
    [readySessionRevision]
  );
  const handlePaymentRecoveryChange = useCallback(
    (recovery: LockerversePaymentRecovery | null) =>
      onPaymentRecoveryChange?.(recovery),
    [onPaymentRecoveryChange]
  );
  const handleResumePaymentFailed = useCallback(() => {
    setActiveResumePayment(undefined);
  }, []);

  useEffect(() => {
    if (session.phase === "loading") {
      setNotice(undefined);
    }
  }, [session.phase]);

  useLayoutEffect(() => {
    if (
      session.phase === "ready" &&
      paymentStateRevisionRef.current !== session.revision
    ) {
      setPaymentState((current) =>
        current === "success" || current === "unresolved" ? current : "idle"
      );
    }
  }, [session.phase, session.revision]);

  const rootClassName = ["lockerverse-payment", className]
    .filter(Boolean)
    .join(" ");
  const effectiveBranding = branding ?? catalog.community;
  const { accent: effectiveAccent, rootStyle } = getPaymentAppearance(
    style,
    effectiveBranding
  );

  if (selectionError) {
    return (
      <section
        {...sectionProps}
        className={rootClassName}
        data-lockerverse-theme={theme}
        role="alert"
        style={rootStyle}
      >
        {selectionError}
      </section>
    );
  }

  const sessionIsSoldOut =
    session.phase === "error" && session.failure === "This option is sold out.";

  if (sessionIsSoldOut || paymentState === "sold_out") {
    return (
      <section
        {...sectionProps}
        className={rootClassName}
        data-lockerverse-theme={theme}
        role="alert"
        style={rootStyle}
      >
        <h1>Sold out</h1>
        <p>This option is sold out.</p>
      </section>
    );
  }

  if (session.phase === "error") {
    return (
      <section
        {...sectionProps}
        className={rootClassName}
        data-lockerverse-theme={theme}
        style={rootStyle}
      >
        <p role="alert">{session.failure}</p>
      </section>
    );
  }

  if (session.phase !== "ready") {
    return (
      <section
        {...sectionProps}
        aria-busy="true"
        aria-label="Payment"
        className={rootClassName}
        data-lockerverse-theme={theme}
        style={rootStyle}
      >
        <PaymentCatalogHeader branding={effectiveBranding} catalog={catalog} />
        {loadError ? (
          <div role="alert">
            <p>{loadError.message}</p>
            <button onClick={onRetry} type="button">
              Retry
            </button>
          </div>
        ) : null}
        <PaymentSkeleton />
      </section>
    );
  }

  return (
    <section
      {...sectionProps}
      aria-label="Payment"
      className={rootClassName}
      data-lockerverse-theme={theme}
      data-payment-state={paymentState}
      ref={appearanceRootRef}
      style={rootStyle}
    >
      <PaymentCatalogHeader branding={effectiveBranding} catalog={catalog} />
      {loadError ? (
        <div role="alert">
          <p>{loadError.message}</p>
          <button onClick={onRetry} type="button">
            Retry
          </button>
        </div>
      ) : null}
      {notice ? (
        <p className="lockerverse-payment__payment-status" role="status">
          {notice}
        </p>
      ) : null}
      {session.quoteFailure ? (
        <div className="lockerverse-payment__payment-status" role="alert">
          <span>{session.quoteFailure}</span>
          <span> The final total will be checked when you submit.</span>
        </div>
      ) : null}
      <ReadyPayment
        accentColor={effectiveAccent}
        appearanceRoot={appearanceRootRef}
        catalog={catalog}
        checkoutItems={session.checkoutItems}
        client={client}
        initialCustomFieldValues={initialCustomFieldValues}
        key={session.revision}
        metadata={metadata}
        onPaymentComplete={onPaymentComplete}
        onPaymentRecoveryChange={handlePaymentRecoveryChange}
        onPaymentUncertain={onPaymentUncertain}
        onQuoteChange={(nextQuote, nextNotice) => {
          setNotice(nextNotice);
          updateCheckout(nextQuote);
        }}
        onResumePaymentFailed={handleResumePaymentFailed}
        onStateChange={handlePaymentStateChange}
        quote={session.displayedQuote}
        resumePayment={activeResumePayment}
        stripeLoader={stripeLoader}
        theme={theme}
        visualStyle={rootStyle}
      />
    </section>
  );
}

function PaymentWidgetState({
  children,
  loading = false,
  viewProps,
}: {
  children: ReactNode;
  loading?: boolean;
  viewProps: PaymentViewProps;
}) {
  const {
    branding,
    className,
    initialCustomFieldValues: _initialCustomFieldValues,
    metadata: _metadata,
    onPaymentComplete: _onPaymentComplete,
    onPaymentRecoveryChange: _onPaymentRecoveryChange,
    onPaymentUncertain: _onPaymentUncertain,
    resumePayment: _resumePayment,
    selection: _selection,
    stripeLoader: _stripeLoader,
    style,
    theme = "dark",
    ...sectionProps
  } = viewProps;
  const accent = normalizeAccentColor(
    style?.["--lockerverse-accent"] ?? branding?.accentColor ?? "#38d77a"
  );
  const accentContrast = getAccentContrast(accent);
  const rootStyle: LockerversePaymentProps["style"] = { ...style };
  if (style?.["--lockerverse-accent"] !== undefined || branding?.accentColor) {
    rootStyle["--lockerverse-accent"] = accent;
  }
  if (
    accentContrast &&
    style?.["--lockerverse-accent-contrast"] === undefined
  ) {
    rootStyle["--lockerverse-accent-contrast"] = accentContrast;
  }

  return (
    <section
      {...sectionProps}
      {...(loading ? { "aria-busy": true, "aria-label": "Payment" } : {})}
      className={["lockerverse-payment", className].filter(Boolean).join(" ")}
      data-lockerverse-theme={theme}
      style={rootStyle}
    >
      {children}
    </section>
  );
}

export function LockerversePayment({
  widget,
  ...viewProps
}: LockerversePaymentProps) {
  if (widget.error && !widget.catalog) {
    return (
      <PaymentWidgetState viewProps={viewProps}>
        <p role="alert">{widget.error.message}</p>
        <button onClick={widget.refresh} type="button">
          Retry
        </button>
      </PaymentWidgetState>
    );
  }
  if (widget.loading) {
    return (
      <PaymentWidgetState loading viewProps={viewProps}>
        {widget.catalog ? (
          <>
            <PaymentCatalogHeader
              branding={viewProps.branding ?? widget.catalog.community}
              catalog={widget.catalog}
            />
            <PaymentCatalogSummary
              catalog={widget.catalog}
              selection={viewProps.selection}
            />
          </>
        ) : null}
        <PaymentSkeleton />
      </PaymentWidgetState>
    );
  }
  if (!widget.catalog) {
    return (
      <PaymentWidgetState loading viewProps={viewProps}>
        <PaymentSkeleton />
      </PaymentWidgetState>
    );
  }
  return (
    <LoadedPayment
      catalog={widget.catalog}
      client={widget.client}
      loadError={widget.error}
      onRetry={widget.refresh}
      {...viewProps}
    />
  );
}
